import { useGetList } from "ra-core";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import type { Prospect } from "../types";
import { prospectStages } from "../prospects/prospectModel";

const toISODate = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Minimal, prospects-only home dashboard for DealEngine.
 * Deliberately avoids the stock B2B widgets (which query dropped tables).
 */
export const DealEngineDashboard = () => {
  const today = new Date();
  const in30 = new Date();
  in30.setDate(today.getDate() + 30);

  const { data: prospects = [], isPending } = useGetList<Prospect>("prospects", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "auction_date", order: "ASC" },
  });

  const { data: upcomingAuctions = [] } = useGetList<Prospect>("prospects", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "auction_date", order: "ASC" },
    filter: {
      "auction_date@gte": toISODate(today),
      "auction_date@lte": toISODate(in30),
    },
  });

  if (isPending) return null;

  const countByStage = prospectStages.map((stage) => ({
    ...stage,
    count: prospects.filter((p) => p.stage === stage.id).length,
  }));

  return (
    <div className="mt-4 flex flex-col gap-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total prospects" value={prospects.length} />
        <StatCard
          label="Upcoming auctions (30d)"
          value={upcomingAuctions.length}
        />
        <StatCard
          label="Active deals"
          value={
            prospects.filter(
              (p) => !["Closed", "Dead"].includes(p.stage),
            ).length
          }
        />
        <StatCard
          label="Closed"
          value={prospects.filter((p) => p.stage === "Closed").length}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="space-y-3">
            <h2 className="text-lg font-semibold">Pipeline</h2>
            {countByStage.map((stage) => (
              <Link
                key={stage.id}
                to={`/prospects?filter=${encodeURIComponent(
                  JSON.stringify({ stage: stage.id }),
                )}`}
                className="flex items-center justify-between text-sm hover:bg-muted rounded px-2 py-1"
              >
                <span>{stage.name}</span>
                <Badge variant="secondary">{stage.count}</Badge>
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3">
            <h2 className="text-lg font-semibold">Upcoming auctions</h2>
            {upcomingAuctions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No auctions in the next 30 days.
              </p>
            ) : (
              upcomingAuctions.map((p) => (
                <Link
                  key={p.id}
                  to={`/prospects/${p.id}/show`}
                  className="flex items-center justify-between text-sm hover:bg-muted rounded px-2 py-1"
                >
                  <span className="truncate">
                    {p.owner_name}
                    {p.property_address ? ` · ${p.property_address}` : ""}
                  </span>
                  <span className="text-muted-foreground shrink-0 ml-2">
                    {p.auction_date}
                  </span>
                </Link>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ label, value }: { label: string; value: number }) => (
  <Card>
    <CardContent className="py-4">
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </CardContent>
  </Card>
);
